import { FC, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { SupportedMarket } from '@estercare/ester-shared'
import { useSelectUserMarket } from '../../state/selectors'
import { useTranslation } from 'react-i18next'
import { theme } from '../../theme/theme'
import { Title2, BodyLarge } from '../styles/Typography'
import { OnClickButton } from '../buttons/onclick/OnClickButton'

interface MarketGuardProps {
  allowedMarkets: SupportedMarket[]
  redirectTo?: string
  showUnavailableMessage?: boolean
}

/**
 * Guard component that restricts access based on user's market.
 *
 * @param allowedMarkets - Array of markets that can access the route
 * @param redirectTo - Optional path to redirect to (default: /portal)
 * @param showUnavailableMessage - If true, shows message instead of redirecting (default: true)
 */
export const MarketGuard: FC<MarketGuardProps> = ({
  allowedMarkets,
  redirectTo = '/portal',
  showUnavailableMessage = true,
}) => {
  const navigate = useNavigate()
  const userMarket = useSelectUserMarket()
  const { t } = useTranslation()

  const isMarketAllowed = userMarket && allowedMarkets.includes(userMarket)

  useEffect(() => {
    // If we're not showing a message and market isn't allowed, redirect
    if (!showUnavailableMessage && !isMarketAllowed) {
      navigate(redirectTo, { replace: true })
    }
  }, [isMarketAllowed, showUnavailableMessage, navigate, redirectTo])

  // If market is allowed, render the protected route
  if (isMarketAllowed) {
    return <Outlet />
  }

  // If we should show an unavailable message, render it
  if (showUnavailableMessage) {
    return (
      <Container>
        <ContentWrapper>
          <Title2>{t('employee.healthCall.notAvailableTitle')}</Title2>
          <BodyLarge>{t('employee.healthCall.notAvailableMessage')}</BodyLarge>
          <OnClickButton onClick={() => navigate('/portal')} text={t('employee.healthCall.returnToHome')} />
        </ContentWrapper>
      </Container>
    )
  }

  // Otherwise, render nothing (redirect will happen via useEffect)
  return null
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
  padding: ${theme.spacing.large}px;
`

const ContentWrapper = styled.div`
  max-width: 600px;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;
  align-items: center;
`
